import type { SceneHost, SceneObject } from "../SceneHost";
import { bboxFromPointerDrag } from "../geometry/shapePaths";
import { getTextSettings, textStyleFields } from "../textSession";
import { applyDocumentContext, documentContextAt } from "../document/documentContext";
import { applyFrameContext, frameAtPoint } from "../frame/frameContext";
import { isBoardEditorChrome, isBoardWidgetChrome } from "./boardWidgetChrome";
import {
  measureTextObjectSize,
  TEXT_BOX_MIN_H,
  TEXT_BOX_MIN_W,
  TEXT_LINE_HEIGHT,
  type TextWidthMode,
} from "../textObject";

/**
 * Ширина пустой надписи — ровно минимальная. Если сделать её шире, рамка
 * на первом же символе заметно сужается до измеренной ширины буквы.
 */
const DEFAULT_WIDTH = TEXT_BOX_MIN_W;

/**
 * Высота пустой надписи — ровно как у одной строки текущим шрифтом.
 * Раньше это было фиксированное число, и на первом же символе рамка
 * заметно «подпрыгивала».
 */
function defaultTextHeight(): number {
  const settings = getTextSettings();
  const measured = measureTextObjectSize(" ", {
    fontSize: settings.fontSize,
    fontFamily: settings.fontFamily,
    lineHeight: TEXT_LINE_HEIGHT,
    fontWeight: settings.fontWeight,
    fontStyle: settings.fontStyle,
  }).h;
  return Math.max(TEXT_BOX_MIN_H, measured);
}
const MIN_DRAG_PX = 4;

export type TextToolBinding = {
  destroy: () => void;
};

export function bindTextTool(host: SceneHost): TextToolBinding {
  let drawing = false;
  let start: { x: number; y: number } | null = null;

  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== "text") return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target)) return;
    if (isBoardWidgetChrome(ev.target)) return;
    if (ev.pointerType === "mouse" && ev.button !== 0) return;
    ev.preventDefault();

    start = host.stagePoint(ev.clientX, ev.clientY);
    drawing = true;
    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
  }

  function onPointerMove(_ev: PointerEvent) {
    /* preview optional — width shown on pointerup */
  }

  async function finish(ev: PointerEvent) {
    if (!drawing || !start) return;
    drawing = false;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel);

    const anchor = start;
    start = null;
    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const dx = Math.abs(pt.x - anchor.x);
    const dy = Math.abs(pt.y - anchor.y);
    const isClick = dx < MIN_DRAG_PX && dy < MIN_DRAG_PX;

    let x = anchor.x;
    let y = anchor.y;
    let w = DEFAULT_WIDTH;
    let h = defaultTextHeight();

    // Клик — как в Miro: рамка растёт вширь по набору и переносит строки только
    // на TEXT_BOX_MAX_W. Протяжка — пользователь задал ширину явно, значит fixed.
    const widthMode: TextWidthMode = isClick ? "auto" : "fixed";

    if (!isClick) {
      const bbox = bboxFromPointerDrag(anchor, pt, false, false);
      if (bbox) {
        x = bbox.x;
        y = bbox.y;
        w = Math.max(80, bbox.w);
        h = Math.max(defaultTextHeight(), bbox.h);
      }
    }

    const settings = getTextSettings();
    const draft: SceneObject = {
      id: crypto.randomUUID(),
      type: "text",
      x,
      y,
      w,
      h,
      text: "",
      style: textStyleFields(settings),
      meta: { textWidthMode: widthMode },
    };

    applyDocumentContext(draft, documentContextAt(host.getObjects(), x + w / 2, y + h / 2));
    applyFrameContext(draft, frameAtPoint(host.getObjects(), x + w / 2, y + h / 2));

    await host.addObject(draft, `text-${draft.id}`);
    host.setSelection([draft.id]);
    host.setActiveTool?.("select");
    // Как у стикера: курсор сразу в блоке, без второго клика.
    host.requestTextEdit?.(draft.id);
  }

  function onPointerUp(ev: PointerEvent) {
    void finish(ev);
  }

  function onPointerCancel(ev: PointerEvent) {
    if (!drawing) return;
    drawing = false;
    start = null;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel);
    void ev;
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);

  return {
    destroy() {
      drawing = false;
      start = null;
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerCancel);
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
    },
  };
}
